import { Component } from '@theme/component';

/**
 * «Todas las reseñas» page: filters the review wall by star rating.
 *
 * Every review is already in the HTML, so filtering is a `hidden` toggle rather than a request —
 * a store with eighty reviews does not need pagination, and the page keeps working with JS off
 * (all reviews visible, no chips that promise anything).
 *
 * The chips are rendered hidden and this component reveals the ones that have reviews behind them,
 * with their count: Liquid would need a second pass over the de-duplicated pool to know, and the
 * browser already has the answer in the DOM.
 *
 * Markup contract (see `sections/all-reviews.liquid`):
 *   <all-reviews>
 *     <button ref="chips[]" data-rating="5" on:click="/filter/5">5 ★</button>
 *     <article ref="cards[]" data-rating="5">…</article>
 *     <p ref="empty" hidden>…</p>
 *   </all-reviews>
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [filters]
 * @property {HTMLButtonElement[]} [chips]
 * @property {HTMLElement[]} [cards]
 * @property {HTMLElement} [empty]
 *
 * @extends Component<Refs>
 */
class AllReviews extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#labelChips();
  }

  /**
   * `on:click="/filter/5"` arrives here as the number 5, not the string: the component runtime
   * parses path arguments. `dataset` values are always strings, so both sides are normalised.
   *
   * @param {string | number} rating - A rating, or 'all'
   */
  filter(rating) {
    const value = String(rating);
    const showAll = value === 'all';
    const cards = this.refs.cards ?? [];

    for (const card of cards) {
      card.hidden = !showAll && card.dataset.rating !== value;
    }

    for (const chip of this.refs.chips ?? []) {
      chip.setAttribute('aria-pressed', String(chip.dataset.rating === value));
    }

    const { empty } = this.refs;
    if (empty) empty.hidden = cards.some((card) => !card.hidden);
  }

  /** Drops the star chips nobody used and reveals the row when the filter can do something. */
  #labelChips() {
    const cards = this.refs.cards ?? [];
    let usable = 0;

    for (const chip of this.refs.chips ?? []) {
      const { rating } = chip.dataset;
      if (!rating || rating === 'all') continue;

      // The number is deliberately not printed: these are the reviews the metafield happens to
      // carry, not the store's real totals, and a count next to the star would read as if it were.
      const count = cards.filter((card) => card.dataset.rating === rating).length;

      chip.hidden = count === 0;
      if (count) usable += 1;
    }

    // One star value on the whole page means the filter can only ever be a no-op.
    const { filters } = this.refs;
    if (filters) filters.hidden = usable < 2;
  }
}

if (!customElements.get('all-reviews')) {
  customElements.define('all-reviews', AllReviews);
}
